import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import { Editor } from "@/forum/Editor";
import { Panel, Btn, Input, Spinner, Empty } from "@/forum/ui";

export default function AdminThreads({ categories, onSaved }) {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [revision, setRevision] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    setError("");
    setResult(null);
    api.get("/forum/admin/threads", { params: { q, page } }).then(({ data }) => {
      if (!active) return;
      if (page > data.pages) setPage(data.pages);
      else setResult(data);
    }).catch((e) => { if (active) setError(errorText(e)); });
    return () => { active = false; };
  }, [q, page, revision, categories]);

  const edit = async (thread) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/forum/threads/${thread.id}`, { params: { page: 1 } });
      if (!data.posts.length) throw new Error("A témának nincs nyitóbejegyzése");
      setEditing({
        id: thread.id, title: data.thread.title, category_id: data.thread.category_id,
        content: data.posts[0].content, pinned: data.thread.pinned, locked: data.thread.locked,
      });
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { id, ...fields } = editing;
      await api.put(`/forum/admin/threads/${id}`, fields);
      setEditing(null);
      setRevision((r) => r + 1);
      await onSaved();
      toast.success("Téma mentve");
    } catch (ex) {
      toast.error(errorText(ex));
    } finally {
      setBusy(false);
    }
  };
  const set = (field, value) => setEditing((current) => ({ ...current, [field]: value }));

  return (
    <section className="space-y-4" data-testid="admin-threads">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/50">Témák</h2>
        <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Keresés témacímre…" aria-label="Témák keresése" className="sm:w-64" data-testid="thread-search" />
      </div>
      {editing && (
        <form onSubmit={save} className="space-y-3 rounded-2xl border border-[rgba(94,177,255,0.3)] p-4" data-testid="admin-thread-form">
          <label className="block space-y-1 text-sm">Téma címe
            <Input value={editing.title} onChange={(e) => set("title", e.target.value)} required minLength={3} maxLength={140} data-testid="admin-thread-title" />
          </label>
          <label className="block space-y-1 text-sm">Kategória
            <select value={editing.category_id} onChange={(e) => set("category_id", e.target.value)} className="block w-full rounded-xl border border-white/10 bg-[#161619] px-3 py-2" data-testid="admin-thread-category">
              {(categories || []).map((c) => <option key={c.id} value={c.id}>{`${c.group} · ${c.name}`}</option>)}
            </select>
          </label>
          <p className="text-sm">Nyitóbejegyzés</p>
          <Editor value={editing.content} onChange={(value) => set("content", value)} testId="admin-thread-editor" />
          <div className="flex gap-5 text-sm">
            <label><input type="checkbox" checked={editing.pinned} onChange={(e) => set("pinned", e.target.checked)} data-testid="admin-thread-pinned" /> Kiemelt</label>
            <label><input type="checkbox" checked={editing.locked} onChange={(e) => set("locked", e.target.checked)} data-testid="admin-thread-locked" /> Lezárt</label>
          </div>
          <div className="flex justify-end gap-2">
            <Btn type="button" variant="ghost" disabled={busy} onClick={() => setEditing(null)}>Mégse</Btn>
            <Btn type="submit" disabled={busy || editing.title.trim().length < 3 || !editing.content.trim() || editing.content.length > 20000} data-testid="admin-thread-save">Mentés</Btn>
          </div>
        </form>
      )}
      {error ? <p role="alert" className="text-red-300">{error} <Btn variant="ghost" onClick={() => setRevision((r) => r + 1)}>Újrapróbálás</Btn></p> : !result ? <Spinner /> : (
        <>
          {!result.threads.length ? <Empty text="Nincs találat." /> : (
            <Panel className="divide-y divide-white/[0.05] overflow-hidden">
              {result.threads.map((t) => (
                <div key={t.id} className="flex items-center gap-3 px-5 py-3" data-testid={`admin-thread-${t.id}`}>
                  <div className="min-w-0 flex-1">
                    <Link to={`/forum/t/${t.id}`} className="font-semibold hover:text-[var(--glow)]">{t.title}</Link>
                    <p className="text-xs text-white/40">{categories?.find((c) => c.id === t.category_id)?.name} · {t.author?.username || "ismeretlen"}{t.pinned ? " · kiemelt" : ""}{t.locked ? " · lezárt" : ""}</p>
                  </div>
                  <Btn variant="ghost" disabled={busy || !categories} onClick={() => edit(t)} data-testid={`edit-thread-${t.id}`}><Pencil size={12} />Szerkesztés</Btn>
                </div>
              ))}
            </Panel>
          )}
          <div className="flex items-center justify-end gap-3 text-sm">
            <Btn variant="ghost" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="threads-prev">Előző</Btn>
            <span>{result.page} / {result.pages} · {result.total} téma</span>
            <Btn variant="ghost" disabled={page >= result.pages} onClick={() => setPage((p) => p + 1)} data-testid="threads-next">Következő</Btn>
          </div>
        </>
      )}
    </section>
  );
}
